/**
 * NewsletterEditor — Full 3-panel editor page.
 * Route: /admin/newsletters/:id
 *
 * Layout:
 *  LEFT  300px  — SectionPanel (section list + add)
 *  CENTER flex  — PreviewCanvas (live preview + drag)
 *  RIGHT 280px  — SettingsPanel (newsletter/section settings)
 *
 * Mobile (<768px):
 *  Single visible panel; sticky bottom nav to switch panels; swipe gestures.
 */
import React, { useEffect, useRef, useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "../../utils/toast";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Save,
  Clock,
  Users,
  Smartphone,
  Monitor,
  Layers,
  Eye,
  Settings,
  LayoutTemplate,
} from "lucide-react";
import { useEditorStore } from "./stores/editorStore";
import { useAutoSave } from "./hooks/useAutoSave";
import { useCollaboration } from "./hooks/useCollaboration";
import { SectionPanel } from "./components/SectionPanel";
import { PreviewCanvas } from "./components/PreviewCanvas";
import { SettingsPanel } from "./components/SettingsPanel";
import { ExportMenu } from "./components/ExportMenu";
import { VersionHistory } from "./components/VersionHistory";
import { CollabPresence } from "./components/CollabPresence";
import { API_URL } from "../../api/api";
import askPrompt from "../../components/em/askPrompt";

// Touch-swipe thresholds
const SWIPE_THRESHOLD = 60;

export default function NewsletterEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    init,
    title,
    dirty,
    saving,
    lastSavedAt,
    activePanel,
    setActivePanel,
    versionPanelOpen,
    toggleVersionPanel,
  } = useEditorStore();

  const canvasRef = useRef(null);
  const { save } = useAutoSave(canvasRef);
  useCollaboration(id);

  const [loading, setLoading] = useState(true);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  // Load newsletter on mount
  useEffect(() => {
    const token = localStorage.getItem("token");
    setLoading(true);
    axios
      .get(`${API_URL}/api/newsletters/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        init(r.data);
      })
      .catch(() => {
        toast.error("Could not load newsletter");
        navigate("/admin/newsletters");
      })
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  // Responsive
  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Touch-swipe panel navigation
  const touchStart = useRef(null);
  const handleTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };
  const handleTouchEnd = useCallback(
    (e) => {
      if (!isMobileView || touchStart.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStart.current;
      const panels = ["sections", "preview", "settings"];
      const idx = panels.indexOf(activePanel);
      if (dx < -SWIPE_THRESHOLD && idx < panels.length - 1)
        setActivePanel(panels[idx + 1]);
      if (dx > SWIPE_THRESHOLD && idx > 0) setActivePanel(panels[idx - 1]);
      touchStart.current = null;
    },
    [isMobileView, activePanel, setActivePanel],
  );

  if (loading) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--em-bg-head)",
        }}
      >
        <div style={{ textAlign: "center", color: "var(--em-fg5)" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>✉️</div>
          <p>Loading newsletter…</p>
        </div>
      </div>
    );
  }

  const panels = [
    { key: "sections", label: "Sections", Icon: Layers },
    { key: "preview", label: "Preview", Icon: Eye },
    { key: "settings", label: "Settings", Icon: Settings },
  ];

  return (
    // Full-view takeover (like Canvas Studio / Profile Generator): a fixed
    // overlay above the app chrome; the back arrow returns to the listing.
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        overflow: isMobileView ? "hidden" : "auto",
        overscrollBehavior: "none",
        display: "flex",
        flexDirection: "column",
        background: "var(--em-bg-muted)",
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* ── Top bar ─────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "0 16px",
          height: 52,
          background: "var(--em-bg-card)",
          borderBottom: "1px solid var(--em-border)",
          flexShrink: 0,
          zIndex: 50,
          position: "sticky",
          top: 0,
        }}
      >
        <button
          onClick={() => navigate("/admin/newsletters")}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "var(--em-fg5)",
            display: "flex",
          }}
        >
          <ArrowLeft size={18} />
        </button>

        {/* Title */}
        <input
          value={title}
          onChange={(e) => useEditorStore.getState().setTitle(e.target.value)}
          style={{
            flex: 1,
            border: "none",
            background: "none",
            fontSize: 15,
            fontWeight: 600,
            color: "#111827",
            outline: "none",
            minWidth: 0,
          }}
          placeholder="Newsletter title…"
        />

        {/* Save status */}
        <span
          style={{
            fontSize: 11,
            color: dirty ? "#f59e0b" : "#6b7280",
            whiteSpace: "nowrap",
          }}
        >
          {saving
            ? "Saving…"
            : dirty
              ? "● Unsaved"
              : lastSavedAt
                ? `Saved ${new Date(lastSavedAt).toLocaleTimeString()}`
                : ""}
        </span>

        {/* Collaboration presence */}
        <CollabPresence />

        {/* Save as template */}
        <button
          onClick={async () => {
            const name = await askPrompt("Name this template", title || "", {
              placeholder: "e.g. Monthly Newsletter",
              okLabel: "Save template",
              required: "Please enter a template name.",
            });
            if (name === null) return; // cancelled
            const token = localStorage.getItem("token");
            try {
              await save(); // persist current edits so the template matches
              await axios.post(
                `${API_URL}/api/newsletters/${id}/save-as-template`,
                { title: name },
                { headers: { Authorization: `Bearer ${token}` } },
              );
              toast.success("Saved as template — find it in the Templates tab");
            } catch {
              toast.error("Could not save as template");
            }
          }}
          title="Save as template"
          style={{
            background: "none",
            border: "1px solid var(--em-border)",
            borderRadius: 6,
            padding: "5px 8px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            color: "#374151",
          }}
        >
          <LayoutTemplate size={13} />{" "}
          <span style={{ display: isMobileView ? "none" : "inline" }}>
            Template
          </span>
        </button>

        {/* Version history */}
        <button
          onClick={toggleVersionPanel}
          title="Version history"
          style={{
            background: "none",
            border: "1px solid var(--em-border)",
            borderRadius: 6,
            padding: "5px 8px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            color: "#374151",
          }}
        >
          <Clock size={13} />{" "}
          <span style={{ display: isMobileView ? "none" : "inline" }}>
            History
          </span>
        </button>

        {/* Manual save */}
        <button
          onClick={() => save()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: dirty ? "#f54b01" : "#f9fafb",
            color: dirty ? "#fff" : "#374151",
            border: "1px solid var(--em-border)",
            borderRadius: 8,
            padding: "7px 14px",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 500,
            transition: "all 0.2s",
          }}
        >
          <Save size={13} />{" "}
          <span style={{ display: isMobileView ? "none" : "inline" }}>
            Save
          </span>
        </button>

        {/* Export */}
        <ExportMenu canvasRef={canvasRef} />
      </div>

      {/* ── Mobile panel tabs ──────────────────────────────────────── */}
      {isMobileView && (
        <div
          style={{
            display: "flex",
            background: "var(--em-bg-card)",
            borderBottom: "1px solid var(--em-border)",
          }}
        >
          {panels.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setActivePanel(key)}
              style={{
                flex: 1,
                padding: "8px 0",
                border: "none",
                cursor: "pointer",
                background: activePanel === key ? "#fff7f5" : "#fff",
                borderBottom:
                  activePanel === key
                    ? "2px solid #f54b01"
                    : "2px solid transparent",
                color: activePanel === key ? "#f54b01" : "#6b7280",
                fontSize: 11,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>
      )}

      {/* ── Panel position indicator (mobile) ─────────────────────── */}
      {isMobileView && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 6,
            padding: "6px 0",
            background: "var(--em-bg-card)",
            borderBottom: "1px solid #f3f4f6",
          }}
        >
          {panels.map(({ key }) => (
            <div
              key={key}
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: activePanel === key ? "#f54b01" : "#d1d5db",
                transition: "background 0.2s",
              }}
            />
          ))}
        </div>
      )}

      {/* ── Main 3-panel area ─────────────────────────────────────── */}
      <div
        style={{
          flex: 1,
          display: "flex",
          position: "relative",
          ...(isMobileView
            ? { overflow: "hidden" }
            : { alignItems: "flex-start" }),
        }}
      >
        {/* LEFT — Sections */}
        <AnimatePresence initial={false}>
          {(!isMobileView || activePanel === "sections") && (
            <motion.div
              key="sections-panel"
              initial={{
                x: isMobileView ? -300 : 0,
                opacity: isMobileView ? 0 : 1,
              }}
              animate={{ x: 0, opacity: 1 }}
              exit={{
                x: isMobileView ? -300 : 0,
                opacity: isMobileView ? 0 : 1,
              }}
              transition={{ duration: 0.2 }}
              style={{
                width: isMobileView ? "100%" : 280,
                flexShrink: 0,
                background: "var(--em-bg-card)",
                borderRight: "1px solid var(--em-border)",
                overflow: "hidden",
                ...(isMobileView
                  ? { height: "100%" }
                  : {
                      position: "sticky",
                      top: 52,
                      height: "calc(100vh - 52px)",
                    }),
              }}
            >
              <SectionPanel />
            </motion.div>
          )}
        </AnimatePresence>

        {/* CENTER — Preview Canvas */}
        <AnimatePresence initial={false}>
          {(!isMobileView || activePanel === "preview") && (
            <motion.div
              key="preview-panel"
              initial={{ opacity: isMobileView ? 0 : 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: isMobileView ? 0 : 1 }}
              transition={{ duration: 0.15 }}
              style={{
                flex: 1,
                minWidth: 0,
                ...(isMobileView ? { overflow: "hidden" } : {}),
              }}
            >
              <PreviewCanvas ref={canvasRef} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* RIGHT — Settings */}
        <AnimatePresence initial={false}>
          {(!isMobileView || activePanel === "settings") && (
            <motion.div
              key="settings-panel"
              initial={{
                x: isMobileView ? 300 : 0,
                opacity: isMobileView ? 0 : 1,
              }}
              animate={{ x: 0, opacity: 1 }}
              exit={{
                x: isMobileView ? 300 : 0,
                opacity: isMobileView ? 0 : 1,
              }}
              transition={{ duration: 0.2 }}
              style={{
                width: isMobileView ? "100%" : 280,
                flexShrink: 0,
                background: "var(--em-bg-card)",
                borderLeft: "1px solid var(--em-border)",
                overflow: "hidden",
                ...(isMobileView
                  ? { height: "100%" }
                  : {
                      position: "sticky",
                      top: 52,
                      height: "calc(100vh - 52px)",
                    }),
              }}
            >
              <SettingsPanel />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Version History slide-over ─────────────────────────────── */}
      <VersionHistory />
    </div>
  );
}
